import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { CrudActions, CrudEventEnum, EntityStatus, EntityTypeEnum, TradingEntityDetail, TradingRelationship, WorkflowActions } from 'api'
import { ActionItem, SplitButtonDropdown } from 'view/components/menus'
import { ActionsButtonProps } from 'view/components/datagrid'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { TradingEntityDialog } from '../TradingEntityDialog'
import { NotificationActions, NotificationType, ShowDialogArgs } from 'store/notification'
import { tradingRelationshipsGetterSelector } from 'selectors/tradingRelationshipSelectors'
import { CorrelatedResponseCallback } from 'store/notification/notificationTypes'
import { Locale, Translation } from 'locale'
import { ViewTradingEntityDialog } from '../TradingEntityDialog/ViewTradingEntityDialog'

export type TradingEntityActionsButtonProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps, ActionsButtonProps<TradingEntityDetail> {}

interface StateProps {
  getTradingRelationships: (tradingEntityId: string) => TradingRelationship[]
}

interface DispatchProps {
  onShowDialog: (dialogArgs: ShowDialogArgs) => unknown
  onShowTradingEntityDialog: (
    entityTypeEnum: EntityTypeEnum,
    entityId: string,
    correlatedResponseCallback?: CorrelatedResponseCallback
  ) => unknown
}

const mapStateToProps = createStructuredSelector({
  getTradingRelationships: tradingRelationshipsGetterSelector
})

const mapDispatchToProps = {
  onShowDialog: NotificationActions.showDialog,
  onShowTradingEntityDialog: WorkflowActions.createEditWorkflow
}

export class TradingEntityActionsButtonComponent extends React.Component<TradingEntityActionsButtonProps> {
  render() {
    const viewTradingEnts = this.props.items.filter(tradingEnt => !this.canEditDetails(tradingEnt))
    const editTradingEnts = this.props.items.filter(tradingEnt => this.canEditDetails(tradingEnt))
    const suspendTradingEnts = this.props.items.filter(tradingEnt => this.canSuspend(tradingEnt))
    const activateTradingEnts = this.props.items.filter(tradingEnt => this.canActivate(tradingEnt))

    const actions: ActionItem[] = [
      {
        label: this.getLabel(Translation.TC_ACTION_VIEW_DETAILS, 1),
        onClick: () => this.handleViewTradingEntDialog(viewTradingEnts),
        enabled: viewTradingEnts.length === 1
      },
      {
        label: this.getLabel(Translation.TC_ACTION_EDIT_DETAILS, 1),
        onClick: () => this.handleShowTradingEntDialog(editTradingEnts),
        enabled: editTradingEnts.length === 1
      },
      {
        label: this.getLabel(Translation.TC_ACTION_SUSPEND_TRADING, suspendTradingEnts.length),
        onClick: () => this.handleSuspend(suspendTradingEnts),
        enabled: suspendTradingEnts.length > 0
      },
      {
        label: this.getLabel(Translation.TC_ACTION_ACTIVATE_TRADING, activateTradingEnts.length),
        onClick: () => this.handleActivateOrgs(activateTradingEnts),
        enabled: activateTradingEnts.length > 0
      }
    ]

    return <SplitButtonDropdown dataTestId={'TradingEntityActionsButton'} actions={actions} maxButtons={1} />
  }

  private getLabel = (baseLabel: Translation, count: number) => {
    const { isGroupRow } = this.props
    const countLabel = isGroupRow ? ` (${count})` : ``
    return `${Locale.translate(baseLabel)}${countLabel}`
  }

  private handleShowTradingEntDialog = (tradingEnts: TradingEntityDetail[]) => {
    const correlatedResponseCallback = (workflowId: string) => {
      this.props.showModal(<TradingEntityDialog tradingEntityEntityId={tradingEnts[0].tradingEntity.id} workflowId={workflowId} />)
    }

    this.props.onShowTradingEntityDialog(EntityTypeEnum.TRADING_ENTITY, tradingEnts[0].tradingEntity.id, correlatedResponseCallback)
  }

  private handleViewTradingEntDialog = (tradingEnts: TradingEntityDetail[]) => {
    this.props.showModal(<ViewTradingEntityDialog tradingEntityEntityId={tradingEnts[0].tradingEntity.id} />)
  }

  private handleSuspend(suspendTradingEnts: TradingEntityDetail[]) {
    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_SUSPEND_TRADING)

    const messageText =
      suspendTradingEnts.length === 1
        ? Locale.translate(Translation.SC_PROMPT_MSG_SUSPEND_SINGLE_TENT, { name: suspendTradingEnts[0].tradingEntity.shortName })
        : Locale.translate(Translation.SC_PROMPT_MSG_SUSPEND_MULTI_TENT, { count: suspendTradingEnts.length })

    const suspendActions = suspendTradingEnts.map(suspendTent =>
      CrudActions.tradingEntityUpsert({
        ...suspendTent.tradingEntity,
        status: EntityStatus.DISABLED
      })
    )

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: titleText,
      messageText: messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: suspendActions
      }
    })
  }

  private handleActivateOrgs(activateTradingEnts: TradingEntityDetail[]) {
    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_ACTIVATE_TRADING)

    const messageText =
      activateTradingEnts.length === 1
        ? Locale.translate(Translation.SC_PROMPT_MSG_ACTIVATE_SINGLE_TENT, { name: activateTradingEnts[0].tradingEntity.shortName })
        : Locale.translate(Translation.SC_PROMPT_MSG_ACTIVATE_MULTI_TENT, { count: activateTradingEnts.length })

    const suspendActions = activateTradingEnts.map(suspendTent =>
      CrudActions.tradingEntityUpsert({
        ...suspendTent.tradingEntity,
        status: EntityStatus.ENABLED
      })
    )

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: titleText,
      messageText: messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: suspendActions
      }
    })
  }

  private canEditDetails(tradingEnt: TradingEntityDetail) {
    return tradingEnt.availableEvents.includes(CrudEventEnum.UPSERT)
  }

  private canSuspend(tradingEnt: TradingEntityDetail) {
    return tradingEnt.tradingEntity.status !== EntityStatus.DISABLED && tradingEnt.availableEvents.includes(CrudEventEnum.UPSERT)
  }

  private canActivate(tradingEnt: TradingEntityDetail) {
    return tradingEnt.tradingEntity.status !== EntityStatus.ENABLED && tradingEnt.availableEvents.includes(CrudEventEnum.UPSERT)
  }
}

export const TradingEntityActionsButton = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(TradingEntityActionsButtonComponent)
)
